/**
 * Onboarding Command Center — Control Portal
 * 5-Step Guided Wizard: Receive Device → Register Customer → Assign + QR → Passport Book → Dispatch
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { controlApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import axios from 'axios';
import BulkDeviceImporter from '@/components/control/BulkDeviceImporter';
import {
  Cpu, Tag, QrCode, BookOpen, Truck, CheckCircle, ArrowRight,
  Loader2, Copy, Plus, ChevronLeft, Package, Layers, Printer
} from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function OnboardingWizardPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1: Receive Device
  const [step1Mode, setStep1Mode] = useState('single'); // 'single' | 'bulk'
  const [deviceForm, setDeviceForm] = useState({ imei_number: '', device_type: 'PRO', manufacturer: '', sim_number: '', module_sn: '' });
  const [receivedDevice, setReceivedDevice] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  // Step 2: Register Client
  const [clientForm, setClientForm] = useState({ company_name: '', company_code: '', contact_name: '', contact_email: '', contact_phone: '', address: '', custom_tagline: 'track verified', customer_logo_url: '' });
  const [registeredClient, setRegisteredClient] = useState(null);
  const [existingClients, setExistingClients] = useState([]);
  const [useExistingClient, setUseExistingClient] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  // Step 3: Assign + QR
  const [unassignedDevices, setUnassignedDevices] = useState([]);
  const [selectedRegistryId, setSelectedRegistryId] = useState('');
  const [assignResult, setAssignResult] = useState(null);

  // Step 4: Passport QR Book
  const [bookCount, setBookCount] = useState(1000);
  const [bookResult, setBookResult] = useState(null);

  // Step 5: Dispatch
  const [dispatchForm, setDispatchForm] = useState({ tracking_number: '', courier: '' });
  const [dispatchResult, setDispatchResult] = useState(null);

  const token = getBearerToken();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/v1/control/onboarding/clients`, { headers });
      setExistingClients(res.data || []);
    } catch (e) {
      console.error('[Onboarding] fetchClients failed:', e?.message || e);
    }
  }, [headers]);

  const fetchUnassigned = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/v1/control/onboarding/unassigned-devices`, { headers });
      setUnassignedDevices(res.data || []);
    } catch (e) {
      console.error('[Onboarding] fetchUnassigned failed:', e?.message || e);
    }
  }, [headers]);

  useEffect(() => {
    fetchClients();
    fetchUnassigned();
  }, [fetchClients, fetchUnassigned]);

  // Step 1: Receive Device
  const handleReceiveDevice = async () => {
    if (!deviceForm.imei_number.trim()) { toast.error('IMEI number is required'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/control/onboarding/step1-receive-device`, deviceForm, { headers });
      setReceivedDevice(res.data);
      toast.success(res.data.message);
      fetchUnassigned();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 2: Register Client
  const handleRegisterClient = async () => {
    if (!clientForm.company_name || !clientForm.company_code || !clientForm.contact_name || !clientForm.contact_email) {
      toast.error('Fill all required fields'); return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/control/onboarding/step2-register-client`, clientForm, { headers });
      setRegisteredClient(res.data);
      toast.success(res.data.message);
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 3: Assign + QR
  const handleAssignDevice = async () => {
    const tenantId = useExistingClient ? selectedClientId : registeredClient?.tenant_id;
    if (!selectedRegistryId || !tenantId) { toast.error('Select device and client'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/control/onboarding/step3-assign-device`, {
        registry_id: selectedRegistryId, tenant_id: tenantId
      }, { headers });
      setAssignResult(res.data);
      toast.success(res.data.message);
      fetchUnassigned();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 4: Passport Book
  const handleGenerateBook = async () => {
    const tenantId = useExistingClient ? selectedClientId : registeredClient?.tenant_id;
    if (!tenantId) { toast.error('No client selected'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/control/onboarding/step4-passport-qr-book`, {
        tenant_id: tenantId, label_count: bookCount
      }, { headers });
      setBookResult(res.data);
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 5: Dispatch
  const handleDispatch = async () => {
    const tenantId = useExistingClient ? selectedClientId : registeredClient?.tenant_id;
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/control/onboarding/step5-dispatch`, {
        tenant_id: tenantId,
        device_registry_ids: assignResult ? [assignResult.registry_id] : [],
        passport_batch_id: bookResult?.batch_id || null,
        tracking_number: dispatchForm.tracking_number || null,
        courier: dispatchForm.courier || null
      }, { headers });
      setDispatchResult(res.data);
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const getTenantName = () => {
    if (registeredClient) return registeredClient.company_name;
    const c = existingClients.find(c => c.id === selectedClientId);
    return c?.name || 'Customer';
  };

  const steps = [
    { num: 1, icon: Cpu, label: 'Receive Device' },
    { num: 2, icon: Tag, label: 'Register Customer' },
    { num: 3, icon: QrCode, label: 'Assign + Device QR' },
    { num: 4, icon: BookOpen, label: 'Passport QR Book' },
    { num: 5, icon: Truck, label: 'Dispatch' }
  ];

  const FormField = ({ label, required, children }) => (
    <div>
      <label className="text-sm text-slate-400 mb-1 block">{label}{required && <span className="text-red-400">*</span>}</label>
      {children}
    </div>
  );

  const inputCls = "bg-slate-900 border-slate-600 text-white placeholder:text-slate-500";

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="onboarding-wizard">
      <div>
        <h1 className="text-2xl font-bold text-white">Onboarding Command Center</h1>
        <p className="text-slate-400 text-sm">Step-by-step: Receive → Register → Assign → QR Book → Dispatch</p>
      </div>

      {/* Step Navigator */}
      <div className="flex items-center gap-1">
        {steps.map((s, idx) => {
          const Icon = s.icon;
          const isActive = step === s.num;
          const isDone = step > s.num;
          return (
            <div key={s.num} className="flex items-center gap-1">
              {idx > 0 && <ArrowRight className={cn("w-3 h-3", isDone ? "text-teal-500" : "text-slate-600")} />}
              <button
                onClick={() => setStep(s.num)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  isActive ? "bg-teal-600 text-white" :
                  isDone ? "bg-teal-900/30 text-teal-400 hover:bg-teal-900/50" :
                  "bg-slate-800 text-slate-500 hover:text-slate-300"
                )}
                data-testid={`step-${s.num}-btn`}
              >
                {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Step 1: Receive Device */}
      {step === 1 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Cpu className="w-5 h-5 text-blue-400" />Step 1 — Receive Device from Manufacturer</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mode toggle: Single vs Bulk */}
            {!receivedDevice && (
              <div className="flex gap-2 mb-4">
                <Button size="sm" onClick={() => setStep1Mode('single')}
                  className={step1Mode === 'single' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}
                  data-testid="single-tab-btn">
                  <Plus className="w-4 h-4 mr-1" />Single Device
                </Button>
                <Button size="sm" onClick={() => setStep1Mode('bulk')}
                  className={step1Mode === 'bulk' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}
                  data-testid="bulk-tab-btn">
                  <Layers className="w-4 h-4 mr-1" />Bulk Import (CSV / Scan)
                </Button>
              </div>
            )}

            {step1Mode === 'bulk' && !receivedDevice ? (
              <BulkDeviceImporter
                onComplete={(res) => { setBulkResult(res); fetchUnassigned(); if (res?.registered_count > 0) setStep(2); }}
                onSingleSwitch={() => setStep1Mode('single')}
              />
            ) : receivedDevice ? (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 font-semibold">Device Received</p>
                  <p className="text-slate-400 text-sm">{receivedDevice.message}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">IMEI</span><span className="font-mono text-white">{receivedDevice.imei}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Type</span><Badge className="bg-blue-900/30 text-blue-400">{receivedDevice.device_type}</Badge></div>
                  <div className="flex justify-between"><span className="text-slate-400">Status</span><Badge className="bg-amber-900/30 text-amber-400">UNASSIGNED</Badge></div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setReceivedDevice(null); setDeviceForm({ imei_number: '', device_type: 'PRO', manufacturer: '', sim_number: '', module_sn: '' }); }} className="border-slate-600 text-slate-300"><Plus className="w-4 h-4 mr-1" />Add Another</Button>
                  <Button onClick={() => setStep(2)} className="bg-teal-600 hover:bg-teal-700 flex-1">Next: Register Customer <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="IMEI Number" required>
                    <Input value={deviceForm.imei_number} onChange={e => setDeviceForm(f => ({ ...f, imei_number: e.target.value }))} placeholder="e.g. 356938035643809" className={inputCls} data-testid="imei-input" />
                  </FormField>
                  <FormField label="Device Type" required>
                    <select value={deviceForm.device_type} onChange={e => setDeviceForm(f => ({ ...f, device_type: e.target.value }))} className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="device-type-select">
                      <option value="PRO">PRO — Reusable. GPS + Temp + Weight</option>
                      <option value="HUB">HUB — Fixed cold-room / warehouse monitor</option>
                      <option value="LITE_PRE">LITE-Pre — Reusable. GPS + Temp (no weight)</option>
                      <option value="LITE">LITE — Single-use temperature sticker</option>
                    </select>
                  </FormField>
                  <FormField label="Manufacturer">
                    <Input value={deviceForm.manufacturer} onChange={e => setDeviceForm(f => ({ ...f, manufacturer: e.target.value }))} placeholder="e.g. Quectel, SIMCom" className={inputCls} />
                  </FormField>
                  <FormField label="SIM Number">
                    <Input value={deviceForm.sim_number} onChange={e => setDeviceForm(f => ({ ...f, sim_number: e.target.value }))} placeholder="SIM card number" className={inputCls} />
                  </FormField>
                  <FormField label="Module Serial">
                    <Input value={deviceForm.module_sn} onChange={e => setDeviceForm(f => ({ ...f, module_sn: e.target.value }))} placeholder="Hardware module S/N" className={inputCls} />
                  </FormField>
                </div>
                <Button onClick={handleReceiveDevice} disabled={loading} className="bg-blue-600 hover:bg-blue-700 w-full h-11" data-testid="receive-device-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cpu className="w-4 h-4 mr-2" />}Register Device
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Register Customer */}
      {step === 2 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Tag className="w-5 h-5 text-teal-400" />Step 2 — Register Customer</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Toggle: New or Existing */}
            <div className="flex gap-2 mb-5">
              <Button variant={!useExistingClient ? "default" : "outline"} size="sm" onClick={() => setUseExistingClient(false)} className={!useExistingClient ? "bg-teal-600" : "border-slate-600 text-slate-300"}>New Customer</Button>
              <Button variant={useExistingClient ? "default" : "outline"} size="sm" onClick={() => setUseExistingClient(true)} className={useExistingClient ? "bg-teal-600" : "border-slate-600 text-slate-300"}>Existing Customer ({existingClients.length})</Button>
            </div>

            {useExistingClient ? (
              <div className="space-y-4">
                <FormField label="Select Customer" required>
                  <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="existing-client-select">
                    <option value="">Choose customer...</option>
                    {existingClients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </FormField>
                <Button onClick={() => setStep(3)} disabled={!selectedClientId} className="bg-teal-600 hover:bg-teal-700 w-full h-11">Next: Assign Device <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            ) : registeredClient ? (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 font-semibold">Customer Registered</p>
                  <p className="text-white">{registeredClient.company_name} ({registeredClient.company_code})</p>
                </div>
                {registeredClient.admin_credentials?.temp_password && (
                  <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4" data-testid="tenant-admin-credentials">
                    <p className="text-amber-300 text-xs uppercase tracking-wider mb-2 font-semibold">
                      🔑 Tenant Super Admin — Share these one-time credentials
                    </p>
                    <div className="bg-slate-900 rounded p-3 space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-400">Login URL</span>
                        <span className="font-mono text-blue-300 text-xs">{window.location.origin}/ops/login</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-400">Email</span>
                        <span className="font-mono text-white">{registeredClient.admin_credentials.email}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-400">Temp Password</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-amber-300 select-all bg-slate-800 px-2 py-1 rounded" data-testid="temp-password-display">
                            {registeredClient.admin_credentials.temp_password}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard?.writeText(
                                `Track0tic Ops Portal\nURL: ${window.location.origin}/ops/login\nEmail: ${registeredClient.admin_credentials.email}\nTemp Password: ${registeredClient.admin_credentials.temp_password}\n\nPlease change your password after first login.`
                              );
                              toast.success('Credentials copied to clipboard');
                            }}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                            data-testid="copy-creds-btn"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-amber-200/80 text-xs mt-2">
                      ⚠️ This password is shown only once. Share it via secure channel. Tenant admin should change it on first login.
                    </p>
                  </div>
                )}
                {registeredClient.admin_credentials?.note && !registeredClient.admin_credentials?.temp_password && (
                  <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 text-sm text-blue-300">
                    ℹ️ {registeredClient.admin_credentials.note}
                  </div>
                )}
                <Button onClick={() => setStep(3)} className="bg-teal-600 hover:bg-teal-700 w-full h-11">Next: Assign Device <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Company Name" required>
                    <Input value={clientForm.company_name} onChange={e => setClientForm(f => ({ ...f, company_name: e.target.value }))} placeholder="e.g. Apollo Pharmacy" className={inputCls} data-testid="company-name-input" />
                  </FormField>
                  <FormField label="Company Code" required>
                    <Input value={clientForm.company_code} onChange={e => setClientForm(f => ({ ...f, company_code: e.target.value.toUpperCase() }))} placeholder="e.g. APOLLO" className={inputCls} maxLength={10} data-testid="company-code-input" />
                  </FormField>
                  <FormField label="Contact Person" required>
                    <Input value={clientForm.contact_name} onChange={e => setClientForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Full name" className={inputCls} />
                  </FormField>
                  <FormField label="Contact Email" required>
                    <Input type="email" value={clientForm.contact_email} onChange={e => setClientForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="email@company.com" className={inputCls} />
                  </FormField>
                  <FormField label="Contact Phone">
                    <Input value={clientForm.contact_phone} onChange={e => setClientForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+91-XXXXXXXXXX" className={inputCls} />
                  </FormField>
                  <FormField label="Address">
                    <Input value={clientForm.address} onChange={e => setClientForm(f => ({ ...f, address: e.target.value }))} placeholder="Company address" className={inputCls} />
                  </FormField>
                  <FormField label="Custom Tagline">
                    <Input value={clientForm.custom_tagline} onChange={e => setClientForm(f => ({ ...f, custom_tagline: e.target.value }))} placeholder="track verified" className={inputCls} />
                    <p className="text-xs text-slate-500 mt-1">Appears on compliance certificates</p>
                  </FormField>
                  <FormField label="Logo URL (optional)">
                    <Input value={clientForm.customer_logo_url} onChange={e => setClientForm(f => ({ ...f, customer_logo_url: e.target.value }))} placeholder="https://..." className={inputCls} />
                  </FormField>
                </div>
                <Button onClick={handleRegisterClient} disabled={loading} className="bg-teal-600 hover:bg-teal-700 w-full h-11" data-testid="register-client-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}Register Client
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Assign Device + Generate Device QR */}
      {step === 3 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><QrCode className="w-5 h-5 text-purple-400" />Step 3 — Assign Device to {getTenantName()} + Generate Device QR</CardTitle>
          </CardHeader>
          <CardContent>
            {assignResult ? (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 font-semibold">Device Assigned & QR Generated</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                  <div className="text-center mb-3">
                    <p className="text-purple-300 text-xs uppercase tracking-wider mb-1">Device Code (Print & Paste on Device)</p>
                    <p className="text-3xl font-mono font-bold text-white tracking-wider">{assignResult.device_code}</p>
                    <p className="text-slate-400 text-sm mt-1">{assignResult.tenant_name} · {assignResult.device_type} · IMEI: {assignResult.imei}</p>
                  </div>
                  <div className="bg-slate-900 rounded p-2 text-xs">
                    <p className="text-slate-500 mb-1">QR Data (encodes owner identity):</p>
                    <p className="font-mono text-purple-400 break-all">{assignResult.qr_data}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="mt-2 text-purple-400" onClick={() => { navigator.clipboard.writeText(assignResult.qr_data); toast.success('QR data copied'); }}>
                    <Copy className="w-3 h-3 mr-1" />Copy QR Data
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setAssignResult(null); setSelectedRegistryId(''); }} className="border-slate-600 text-slate-300"><Plus className="w-4 h-4 mr-1" />Assign Another</Button>
                  <Button onClick={() => setStep(4)} className="bg-teal-600 hover:bg-teal-700 flex-1">Next: Passport QR Book <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <FormField label="Select Unassigned Device" required>
                  <select value={selectedRegistryId} onChange={e => setSelectedRegistryId(e.target.value)} className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="select-device-assign">
                    <option value="">Choose device...</option>
                    {unassignedDevices.map(d => (
                      <option key={d.id} value={d.id}>{d.device_uid} — IMEI: {d.imei} ({d.device_type})</option>
                    ))}
                  </select>
                </FormField>
                {unassignedDevices.length === 0 && (
                  <p className="text-amber-400 text-sm">No unassigned devices. Go to Step 1 to receive a device first.</p>
                )}
                <p className="text-xs text-slate-500">A unique alphanumeric Device Code (TRK-{'{TYPE}'}-{'{CLIENT}'}-{'{RANDOM}'}) will be generated. This QR, when scanned, identifies: who owns this device, what type it is, and its IMEI.</p>
                <Button onClick={handleAssignDevice} disabled={loading || !selectedRegistryId} className="bg-purple-600 hover:bg-purple-700 w-full h-11" data-testid="assign-device-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}Assign & Generate Device QR
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Passport QR Book */}
      {step === 4 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><BookOpen className="w-5 h-5 text-amber-400" />Step 4 — Generate Passport QR Book for {getTenantName()}</CardTitle>
          </CardHeader>
          <CardContent>
            {bookResult ? (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 font-semibold">Passport QR Book Generated</p>
                  <p className="text-white text-lg font-bold">{bookResult.total_labels} DORMANT Labels</p>
                  <p className="text-slate-400 text-sm">Batch: {bookResult.batch_number}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-500 text-xs mb-2">Sample Label UIDs:</p>
                  <div className="flex flex-wrap gap-1">
                    {bookResult.sample_labels?.map((l, idx) => (
                      <span key={`lbl-${idx}`} className="font-mono text-xs text-amber-400 bg-amber-900/20 px-2 py-1 rounded">{l.uid}</span>
                    ))}
                    <span className="text-slate-500 text-xs px-2 py-1">...and {bookResult.total_labels - 5} more</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => window.open(`${API_BASE}/api/v1/control/print/passport-book/${bookResult.batch_id}`, '_blank')}
                    variant="outline"
                    className="border-blue-600 text-blue-300 hover:bg-blue-900/20"
                    data-testid="print-passport-book-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />Print Book ({bookResult.total_labels})
                  </Button>
                  <Button onClick={() => setStep(5)} className="bg-teal-600 hover:bg-teal-700 flex-1 h-11">
                    Next: Dispatch to Customer <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <FormField label="Number of Passport QR Labels" required>
                  <Input type="number" value={bookCount} onChange={e => setBookCount(parseInt(e.target.value) || 1000)} min={100} max={10000} className={inputCls} data-testid="book-count-input" />
                  <p className="text-xs text-slate-500 mt-1">These will be printed as a roll/book. Each label has a unique UUID QR. All start as DORMANT.</p>
                </FormField>
                <Button onClick={handleGenerateBook} disabled={loading} className="bg-amber-600 hover:bg-amber-700 w-full h-11" data-testid="generate-book-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}Generate {bookCount} Passport QR Labels
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Dispatch */}
      {step === 5 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Truck className="w-5 h-5 text-green-400" />Step 5 — Dispatch to {getTenantName()}</CardTitle>
          </CardHeader>
          <CardContent>
            {dispatchResult ? (
              <div className="space-y-4">
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-6 text-center">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <p className="text-green-400 font-bold text-lg">Dispatch Complete</p>
                  <p className="text-white">{dispatchResult.message}</p>
                  {dispatchResult.tracking_number && <p className="text-slate-400 text-sm mt-2">Tracking: {dispatchResult.tracking_number} ({dispatchResult.courier})</p>}
                </div>
                <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Client</span><span className="text-white">{dispatchResult.tenant_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Devices</span><span className="text-white">{dispatchResult.devices_dispatched?.join(', ') || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">QR Book</span><span className="text-white">{dispatchResult.passport_batch ? 'Included' : 'Not included'}</span></div>
                </div>
                <Button onClick={() => { setStep(1); setReceivedDevice(null); setRegisteredClient(null); setAssignResult(null); setBookResult(null); setDispatchResult(null); }} className="bg-blue-600 hover:bg-blue-700 w-full h-11" data-testid="start-new-btn">
                  <Package className="w-4 h-4 mr-2" />Start New Onboarding
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                  <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Shipment Summary</p>
                  <div className="flex justify-between"><span className="text-slate-400">Client</span><span className="text-white">{getTenantName()}</span></div>
                  {assignResult && <div className="flex justify-between"><span className="text-slate-400">Device</span><span className="font-mono text-purple-400">{assignResult.device_code}</span></div>}
                  {bookResult && <div className="flex justify-between"><span className="text-slate-400">QR Book</span><span className="text-amber-400">{bookResult.batch_number} ({bookResult.total_labels} labels)</span></div>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Tracking Number">
                    <Input value={dispatchForm.tracking_number} onChange={e => setDispatchForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="e.g. DTDC123456" className={inputCls} />
                  </FormField>
                  <FormField label="Courier">
                    <Input value={dispatchForm.courier} onChange={e => setDispatchForm(f => ({ ...f, courier: e.target.value }))} placeholder="e.g. BlueDart, DTDC" className={inputCls} />
                  </FormField>
                </div>
                <Button onClick={handleDispatch} disabled={loading} className="bg-green-600 hover:bg-green-700 w-full h-11" data-testid="dispatch-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}Dispatch Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Back button */}
      {step > 1 && !dispatchResult && (
        <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-slate-400 hover:text-white">
          <ChevronLeft className="w-4 h-4 mr-1" />Back to Step {step - 1}
        </Button>
      )}
    </div>
  );
}
